import { Component, Vue } from "vue-property-decorator";
import moment from "moment";
import { gql } from "graphql-tag";
import { complaintApi } from "@/providers/apis/complaint";
import { COMPLAINT_FORM_QUERY } from "@/graphql/complaint-form-query";
import { DEFAULT_FORM_FIELDS } from "./complaint-form-fields";
import {
  ComplaintAttachment,
  ComplaintFormCopy,
  ComplaintPayload,
} from "./complaint.interface";

/**
 * Wording used when Strapi has no value for a field — either because the
 * content type has not been created yet or the field was left blank.
 */
const DEFAULT_COPY: Required<ComplaintFormCopy> = {
  // CADRE rather than a program name: the form is shared, so the heading
  // carries the umbrella brand unless Strapi supplies something narrower.
  program_title: "CADRE",
  form_title: "Complaint Form",
  // No default: the form is shared across programs, so a CPRS-specific
  // handbook link does not belong on it unless Strapi supplies one.
  handbook_note: "",
  legal_notice:
    "Please note that pursuant to Tenn. Code Ann. &sect; 10-7-503, all state records are considered open for public inspection, unless otherwise protected under the law. Accordingly, the State cannot and does not guarantee the confidentiality of this complaint, any notes, files, reports or other documents received by the State or in possession of the State in conjunction with this complaint.",
  contact_section_title: "How can we reach you?",
  contact_section_description:
    "We may need to contact you for further information. Please enter your contact details.",
  complaint_section_title: "Complaint Details",
  complaint_section_description:
    "Please fill out the information below with as much detail as possible. You can upload any supporting documents or files below, if desired.",
  supporting_documents_note:
    "Optional: you may upload PDF files or images as supporting documents for this complaint.",
  submitted_title: "Complaint Submitted.",
  submitted_message:
    "Thank you for submitting your complaint. It has been received and will be reviewed in accordance with our ethics review process. Your submission ID is {{id}}.",
  submitted_follow_up: "We will reach out for more information as needed.",
};

// Matches spring.servlet.multipart.max-file-size on the workflow API. This one
// is a real backend limit, so it stays: each file is uploaded in its own
// request, and one over 10 MB is rejected there regardless of what we allow.
const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB

// Supporting documents are often photographs rather than scans, so images are
// accepted alongside PDFs. SVG is deliberately left out: it can carry script,
// and these files are served back to reviewers.
//
// There is no cap on how many files may be attached. Uploads go one file per
// request, so a long list costs more round trips but never a larger one, and
// max-request-size on the API is not a factor.
const ACCEPTED_TYPES = new Set([
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
]);
const ACCEPTED_LABEL = "PDF, PNG, JPG, GIF or WEBP";

@Component({})
export default class ComplaintFormComponent extends Vue {
  public payLoadData: ComplaintPayload = {
    fullName: "",
    email: "",
    phoneNumber: "",
    eventDate: "",
    againstName: "",
    details: "",
    attachment: [],
  };

  /** Confirm-email is checked in the UI only; it is not sent to the API. */
  public confirmEmail = "";

  public selectedFiles: File[] = [];
  public fileError = "";

  public isLoading = false;
  public checkValidForm = false;
  public errorMessage = "";

  /** Set once the complaint is filed; swaps the form for the confirmation. */
  public acknowledgmentId = "";

  /**
   * Program the complaint is filed against. Optional: without it the complaint
   * arrives unrouted and staff assign a program. `?program=cprs` presets it,
   * which is how a program-specific entry point links here.
   */
  public program = "";

  /** Merged CMS copy; starts as the defaults so the form renders immediately. */
  public copy: Required<ComplaintFormCopy> = { ...DEFAULT_COPY };

  /** Field definition, from Strapi formFields when present. */
  public formFields: any = DEFAULT_FORM_FIELDS;

  public get formSections(): any[] {
    return this.formFields?.fields || [];
  }

  /**
   * Inputs are generated, so they read and write the payload by name rather
   * than through a fixed v-model. confirmEmail is UI-only and never sent.
   */
  public fieldValue(name: string): any {
    if (name === "confirmEmail") {
      return this.confirmEmail;
    }
    return (this.payLoadData as any)[name];
  }

  public setFieldValue(name: string, value: any) {
    if (name === "confirmEmail") {
      this.confirmEmail = value;
      return;
    }
    (this.payLoadData as any)[name] = value;
  }

  /** Read-only values rendered by a `static-value` field. */
  public staticValue(name: string): string {
    if (name === "todaysDate") {
      return this.todaysDate;
    }
    return "";
  }

  /**
   * Date inputs are capped at today: a complaint describes something that has
   * already happened, so a future event date is never valid. A field can opt
   * out with `allowFuture: true` in formFields.
   */
  public dateMax(input: any): string | undefined {
    if (input?.propBind?.type !== "date" || input?.allowFuture === true) {
      return undefined;
    }
    return moment().format("YYYY-MM-DD");
  }

  public get todaysDate(): string {
    return moment().format("DD MMMM YYYY");
  }

  /** Acknowledgment message with {{id}} replaced by the returned code. */
  public get submittedMessage(): string {
    return this.copy.submitted_message.replace(
      /\{\{\s*id\s*\}\}/g,
      this.acknowledgmentId
    );
  }

  public get isSubmitted(): boolean {
    return !!this.acknowledgmentId;
  }

  public get emailMismatch(): boolean {
    return (
      this.checkValidForm &&
      !!this.payLoadData.email &&
      this.confirmEmail !== this.payLoadData.email
    );
  }

  /**
   * Same sections as the live form, flattened to a label/value list and with
   * the UI-only confirmEmail dropped, so the printable copy reads exactly like
   * the form the filer submitted.
   */
  public get printSections(): any[] {
    return this.formSections.map((section: any) => {
      const fields: any[] = [];
      (section.inputs || []).forEach((row: any[]) => {
        row.forEach((input: any) => {
          if (input.name !== "confirmEmail") {
            fields.push(input);
          }
        });
      });
      return {
        sectionTitle: section.sectionTitle,
        sectionDescription: section.sectionDescription,
        fields,
      };
    });
  }

  /** The submitted value for a field, formatted the way the PDF should read. */
  public printValue(input: any): string {
    if (input.field === "static-value") {
      return this.staticValue(input.name);
    }
    const raw = this.fieldValue(input.name);
    if (raw === undefined || raw === null || `${raw}`.trim() === "") {
      return "—";
    }
    if (input?.propBind?.type === "date") {
      const parsed = moment(raw);
      return parsed.isValid() ? parsed.format("DD MMMM YYYY") : `${raw}`;
    }
    return `${raw}`;
  }

  /**
   * Hands the printable copy to the browser's print-to-PDF. The print
   * stylesheet hides the app shell and shows only .complaint-print, so the
   * saved PDF is a clean rendering of the filed complaint.
   */
  public downloadPdf() {
    // Blank the document title during printing so the browser's print header
    // doesn't show "Cadre" in the top centre. The date/URL are still browser
    // controlled (unchecking "Headers and footers" removes those).
    const originalTitle = document.title;
    const restoreTitle = () => {
      document.title = originalTitle;
      window.removeEventListener("afterprint", restoreTitle);
    };
    document.title = " ";
    window.addEventListener("afterprint", restoreTitle);
    window.print();
    // Fallback restore in case afterprint does not fire.
    setTimeout(restoreTitle, 1000);
  }

  public async created() {
    const queryProgram = this.$route.query.program;
    if (typeof queryProgram === "string") {
      this.program = queryProgram;
    }
    // A visitor can land here directly, without passing the home page that
    // normally fetches the anonymous token, so make sure one exists.
    await complaintApi.ensureAnonymousToken().catch(() => {
      // Leave it: submit surfaces the failure with a usable message.
    });
    await this.fetchCopy();
  }

  /**
   * Pulls the wording from Strapi. Failures are deliberately swallowed: the
   * form must still work if the content type has not been created, so the
   * built-in defaults simply stay in place.
   */
  private async fetchCopy() {
    try {
      const result = await this.$apollo.query({
        query: gql`query ${COMPLAINT_FORM_QUERY}`,
      });
      // Collection type, but only the first entry is used as the copy source.
      const attributes = result?.data?.complaintForms?.data?.[0]?.attributes;
      if (!attributes) {
        return;
      }
      if (attributes.formFields && attributes.formFields.fields) {
        this.formFields = attributes.formFields;
      }

      const merged: any = { ...DEFAULT_COPY };
      Object.keys(DEFAULT_COPY).forEach((key) => {
        const value = attributes[key];
        if (value !== null && value !== undefined && `${value}`.trim() !== "") {
          merged[key] = value;
        }
      });
      this.copy = merged;
    } catch (error) {
      // Content type absent or query rejected — keep the defaults.
    }
  }

  public onFilesSelected(event: any) {
    this.fileError = "";
    const chosen: File[] = Array.from(event?.target?.files || []);
    if (!chosen.length) {
      return;
    }

    for (const file of chosen) {
      if (!ACCEPTED_TYPES.has(file.type)) {
        this.fileError = `${file.name} is not a supported file type. Attach ${ACCEPTED_LABEL} files.`;
        continue;
      }
      if (file.size > MAX_FILE_BYTES) {
        this.fileError = `${file.name} is larger than 10 MB.`;
        continue;
      }
      this.selectedFiles.push(file);
    }
    // Reset so re-picking the same file still fires a change event.
    if (event?.target) {
      event.target.value = null;
    }
  }

  public removeFile(index: number) {
    this.selectedFiles.splice(index, 1);
    this.fileError = "";
  }

  public async onSubmit() {
    this.errorMessage = "";
    this.checkValidForm = true;

    const valid = await this.$validator.validateAll();
    if (!valid || this.emailMismatch) {
      this.$nextTick(() => {
        const el = document.querySelector(".field-error") as HTMLElement;
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });
      return;
    }

    this.isLoading = true;
    try {
      // Upload first: the complaint stores file references, so a failed upload
      // must not leave a complaint pointing at documents that do not exist.
      const attachment: ComplaintAttachment[] = [];
      for (const file of this.selectedFiles) {
        // The upload endpoint takes MultipartFile[] and answers with a list,
        // even for a single file, so unwrap the first entry.
        const uploaded = await complaintApi.uploadFile(file);
        const stored = Array.isArray(uploaded) ? uploaded[0] : uploaded;
        if (!stored || !stored.fileName) {
          throw new Error(`Upload failed for ${file.name}`);
        }
        attachment.push({
          fileName: stored.fileName,
          originalFileName: stored.originalFileName || file.name,
        });
      }
      this.payLoadData.attachment = attachment;

      // The form no longer asks the filer to pick a program: the complaint is
      // submitted unrouted and staff assign the program later. A ?program=
      // query still routes it via the endpoint path.
      const result = await complaintApi.submitComplaint(
        this.payLoadData,
        this.program
      );
      this.acknowledgmentId = result?.code || "";
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error: any) {
      this.errorMessage =
        error?.response?.data?.message ||
        "Something went wrong while submitting your complaint. Please try again.";
    } finally {
      this.isLoading = false;
    }
  }
}
